import {
  buildReviewDubMap,
  readReviewDubPlayhead,
} from '@src/editor/dubbing/reviewDubPlayhead';
import {
  lineLevels,
  lineOverride,
  sameDubSegmentLevels,
  withDubSegmentLevel,
} from '@src/editor/dubbing/segmentLevels';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { ReviewLevelsLine } from './ReviewPlayerAudioPickers';
import type { DubLevels } from '@src/components/aiVoices/DubLevelSlider';
import type { DubOnReviewClock } from '@src/editor/dubbing/reviewDubPlayhead';
import type {
  DubSegmentLevel,
  DubSegmentLevels,
} from '@src/editor/dubbing/segmentLevels';

/** `level` with its Original replaced by `originalVolume` (none if absent). */
function withOriginalOf(
  level: DubSegmentLevel | null,
  originalVolume: number | undefined,
): DubSegmentLevel | null {
  const next: DubSegmentLevel = {};
  if (level?.voiceVolume !== undefined) next.voiceVolume = level.voiceVolume;
  if (originalVolume !== undefined) next.originalVolume = originalVolume;
  return Object.keys(next).length > 0 ? next : null;
}

export interface UseReviewLevelsLineInput {
  /** Off on the whole-story entry, which has no clip to save a line to. */
  enabled: boolean;
  /** The dub the review's player makes heard, on its clock. */
  dub: DubOnReviewClock | null;
  currentTime: number;
  /**
   * The clip-wide levels the review is playing. A line's own levels, saved or
   * not, are measured against these: what differs from what is heard is what
   * the line holds of its own.
   */
  levels: DubLevels;
  /**
   * The clip-wide levels the clip is saved with. Apply to Line keeps a level
   * that differs from these too, so a line heard at an unapplied clip level
   * keeps it once the review closes.
   */
  appliedLevels: DubLevels;
  /** The lines the clip is saved with at their own levels. */
  appliedSegmentLevels: DubSegmentLevels | undefined;
  /**
   * The Original level is shown, on Both. Off Both it is neither shown nor
   * heard, so nothing done there saves or moves a line's Original.
   */
  showOriginal: boolean;
  /** Save the clip's whole set of line levels. */
  onSave: (segmentLevels: DubSegmentLevels) => void;
}

/**
 * Dubbing Review's per-line levels: the levels the review plays for each line
 * (transient, like its clip-wide levels, until applied), and the line under
 * the playhead as the Levels panel shows it.
 *
 * The line is the one playing, else the next to start — the rule the review's
 * reading already follows. "This line only" pins it, so a playhead moving on
 * does not carry the sliders to another line.
 */
export function useReviewLevelsLine({
  enabled,
  dub,
  currentTime,
  levels,
  appliedLevels,
  appliedSegmentLevels,
  showOriginal,
  onSave,
}: UseReviewLevelsLineInput): {
  segmentLevels: DubSegmentLevels | undefined;
  levelsLine: ReviewLevelsLine | undefined;
} {
  const [segmentLevels, setSegmentLevels] = useState(appliedSegmentLevels);
  const [pinnedId, setPinnedId] = useState<string | null>(null);

  const dubMap = useMemo(
    () => (enabled && dub ? buildReviewDubMap(dub, [], []) : null),
    [enabled, dub],
  );
  const activeId = dubMap
    ? readReviewDubPlayhead(dubMap, currentTime).activeLineId
    : null;
  const lineId =
    pinnedId && dubMap?.lineById.has(pinnedId) ? pinnedId : activeId;
  const line = lineId ? dubMap?.lineById.get(lineId) : undefined;

  // Stable, so the panel's let-go-on-close cleanup runs on close only.
  const lineIdRef = useRef(lineId);
  lineIdRef.current = lineId;
  const pin = useCallback(
    (pinned: boolean) => setPinnedId(pinned ? lineIdRef.current : null),
    [],
  );

  const levelsLine = useMemo<ReviewLevelsLine | undefined>(() => {
    if (!line) return undefined;
    const segmentId = line.segmentId;
    const heard = lineLevels(segmentLevels, segmentId, levels);
    const saved = appliedSegmentLevels?.[segmentId];
    // What Apply to Line saves for the line heard at `at`: off Both, the
    // Original stays as saved.
    const toApply = (at: DubLevels) => {
      const override = lineOverride(at, levels, appliedLevels);
      return showOriginal
        ? override
        : withOriginalOf(override, saved?.originalVolume);
    };
    // Worked out exactly as Apply to Line saves, so the button is live
    // exactly when that changes something.
    const pending = toApply(heard);
    return {
      segmentId,
      levels: heard,
      onChange: heard =>
        setSegmentLevels(current =>
          withDubSegmentLevel(current, segmentId, lineOverride(heard, levels)),
        ),
      onApply: at => {
        const applied = toApply(at);
        const next = withDubSegmentLevel(
          appliedSegmentLevels,
          segmentId,
          applied,
        );
        // An unapplied Original hidden off Both stays heard in the review.
        setSegmentLevels(current =>
          withDubSegmentLevel(
            current,
            segmentId,
            showOriginal
              ? applied
              : withOriginalOf(applied, current?.[segmentId]?.originalVolume),
          ),
        );
        // `{}` rather than nothing: a save of no lines clears them.
        onSave(next ?? {});
      },
      unapplied: !sameDubSegmentLevels(
        pending ? { [segmentId]: pending } : undefined,
        saved ? { [segmentId]: saved } : undefined,
      ),
      revert: () => setSegmentLevels(appliedSegmentLevels),
      // Measured against the saved clip levels: the clip goes back to them as
      // the line takes what it was heard at.
      takeClipLevels: () =>
        setSegmentLevels(current => {
          const taken = lineOverride(heard, appliedLevels);
          return withDubSegmentLevel(
            current,
            segmentId,
            showOriginal
              ? taken
              : withOriginalOf(taken, current?.[segmentId]?.originalVolume),
          );
        }),
      pin,
      hasOwnLevels: !!segmentLevels?.[segmentId],
      hasSavedLevels: !!saved,
    };
  }, [
    line,
    segmentLevels,
    levels,
    appliedLevels,
    appliedSegmentLevels,
    showOriginal,
    onSave,
    pin,
  ]);

  return { segmentLevels, levelsLine };
}
